/**
 * @file fonts-tab-params.ts
 * @description Параметры адреса вкладки «Оформление → Шрифты» (phase19.16;
 * SB10, дополнение 2026-10-11; uiux-fonts-usage §3, §4): подсказка о шрифте
 * в панели элемента открывает вкладку ссылкой
 * `builderLink({ panel: "appearance", sub: "fonts" })` с добавкой
 *   - `font=<heading|body|accent>` — какую роль раскрыть и подсветить;
 *   - `back=<адрес конструктора к элементу>` и `backLabel=<имя элемента>` —
 *     ссылка «← К элементу «…»».
 *
 * Чужое значение `font` — как будто его нет; `back` принимается только
 * путём конструктора магазина «/shop/…» после разбора `URL` (`safeBackHref`),
 * чтобы ссылкой из адреса нельзя было увести на чужой сайт. При смене
 * вкладки «Оформления» `back`/`backLabel` переносятся (`fontsTabCarry`).
 */

import { FONT_ROLE_KEYS, FONT_ROLES, type FontRole } from "@chaiberi/shared";

export interface FontsTabBack {
  href: string;
  label: string;
}

export interface FontsTabParams {
  /** Роль, которую раскрыть, прокрутить к ней и подсветить. */
  focus: FontRole | null;
  /** Возврат к элементу, из которого пришли; null — ссылки нет. */
  back: FontsTabBack | null;
}

function isFontRole(raw: string | null): raw is FontRole {
  return raw !== null && (FONT_ROLE_KEYS as readonly string[]).includes(raw);
}

/** Условный адрес-основа для разбора: свой «сайт», которого не бывает у настоящих ссылок. */
const PARSE_BASE = "http://commora.invalid";

/**
 * Безопасный адрес возврата: разбираем как браузер (он выкидывает табуляцию
 * и переводы строк — «/\t/evil.com» превращается в «//evil.com»), принимаем
 * только свой условный origin и путь конструктора магазина «/shop/…».
 * Отдаём путь и запрос без «#…»; чужое — null (ревью 2026-10-11).
 */
export function safeBackHref(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw, PARSE_BASE);
  } catch {
    return null;
  }
  if (url.origin !== PARSE_BASE || !url.pathname.startsWith("/shop/")) return null;
  return url.pathname + url.search;
}

export function parseFontsTabParams(params: URLSearchParams): FontsTabParams {
  const font = params.get("font");
  const raw = params.get("back");
  const href = raw ? safeBackHref(raw) : null;
  return {
    focus: isFontRole(font) ? font : null,
    back: href ? { href, label: params.get("backLabel")?.trim() ?? "" } : null,
  };
}

/** Ключи возврата к элементу, которые переносятся при смене вкладки «Оформления». */
const BACK_KEYS = ["back", "backLabel"] as const;

/** `back`/`backLabel` из адреса — чтобы ссылка «← К элементу» не пропала при смене вкладки. */
export function fontsTabCarry(params: URLSearchParams): Record<string, string> {
  const carry: Record<string, string> = {};
  for (const key of BACK_KEYS) {
    const value = params.get(key);
    if (value !== null) carry[key] = value;
  }
  return carry;
}

/** Текст ссылки возврата: «К элементу «Заголовок»»; без имени — «К элементу». */
export function backLinkText(label: string): string {
  return label ? `К элементу «${label}»` : "К элементу";
}

/** Заголовок окна каталога шрифтов по роли: «Шрифт заголовков», «Шрифт акцента». */
export function fontDialogTitle(role: FontRole): string {
  return `Шрифт ${FONT_ROLES[role].genitive}`;
}
